import {ProminentBannerSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Button, FONT_SIZES} from '@aboutyou/ui-components';
import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React from 'react';
import {useActivatedCouponState} from '../../../../../features/Coupons/useActivatedCouponState';
import {useCouponStickyBarOpenStateManager} from '../CouponStickyBar/utils/useCouponStickyBarOpenStateManager';
import {useGetHighestCouponDiscountValue} from '../utils/useGetHighestCouponDiscountValue';

const SLIDE_IN_ANIMATION_DURATION = 500;

export const CouponHintView: React.FC<
  ProminentBannerSection & {productId: number; isInMiniADPDialog: boolean}
> = ({type, productId, isInMiniADPDialog}) => {
  const {t} = useTranslation();
  const {open} = useCouponStickyBarOpenStateManager(isInMiniADPDialog);

  const couponSlider =
    type?.$case === 'couponSlider' ? type.couponSlider : undefined;

  const highestDiscountValue = useGetHighestCouponDiscountValue({
    coupons: couponSlider?.coupons ?? [],
  });

  const {couponPromotionId} = useActivatedCouponState(productId);
  const discountValue = {value: highestDiscountValue, unit: '%'};

  const onCouponHintClick = () => {
    open();
  };

  if (!couponSlider || couponPromotionId) {
    return null;
  }

  return (
    <Wrapper
      className={cx(couponPromotionId ? SlideOutAnimation : SlideInAnimation)}
    >
      <CouponBadgeWrapper
        data-testid="couponHint"
        $backgroundColor={couponSlider.backgroundColor.hex}
        $foregroundColor={couponSlider.foregroundColor.hex}
        onClick={onCouponHintClick}
        size="fit-width"
      >
        {t('Coupon reduction. Save up to {value}{unit} 🎉', discountValue)}
      </CouponBadgeWrapper>
    </Wrapper>
  );
};

const SlideInAnimation = css`
  @keyframes slide-in {
    0% {
      height: 0;
    }
    100% {
      height: 50px;
    }
  }

  animation: slide-in ${SLIDE_IN_ANIMATION_DURATION}ms
    ${SLIDE_IN_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const SlideOutAnimation = css`
  @keyframes slide-out {
    0% {
      height: 50px;
    }
    100% {
      height: 0;
    }
  }

  animation: slide-out ${SLIDE_IN_ANIMATION_DURATION}ms
    ${SLIDE_IN_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const Wrapper = styled.div`
  position: relative;
  height: 0;
  overflow: hidden;
`;

const CouponBadgeWrapper = styled(Button)<{
  $backgroundColor: string;
  $foregroundColor: string;
}>`
  --background-color: ${({$backgroundColor}) => $backgroundColor};
  font-size: ${FONT_SIZES.px12};
  padding: 8px 10px;
  position: relative;
  margin-top: 6px;
  overflow: visible;

  &&&& {
    height: 34px;
    --color: ${({$foregroundColor}) => $foregroundColor};

    &:hover {
      --background-color: ${({$backgroundColor}) => $backgroundColor};
      --color: ${({$foregroundColor}) => $foregroundColor};
    }
  }

  ::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    top: -5px;
    left: clamp(0px, 17%, 50px);

    border-bottom: 6px solid;
    border-bottom-color: ${({$backgroundColor}) => $backgroundColor};
  }
`;
